import axios from 'axios';
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { appConfig } from '../config/app';
import { authService } from './auth';
import keycloak from '../config/keycloak';
import type { ApiError } from '../types';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: appConfig.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Request interceptor - add JWT token to all requests
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Try to refresh token if it's about to expire
    try {
      await keycloak.updateToken(30); // Refresh if expires in 30 seconds
    } catch (error) {
      console.warn('Token refresh failed in request interceptor:', error);
    }

    // Get current access token
    const token = authService.getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors globally
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    // Handle 401 Unauthorized - try to refresh token
    if (error.response?.status === 401) {
      // Don't retry for /auth/authorize - it might be a claims issue, not token expiry
      const isAuthEndpoint = error.config?.url?.includes('/auth/authorize');

      if (!isAuthEndpoint) {
        try {
          const refreshed = await keycloak.updateToken(-1); // Force refresh

          if (refreshed && error.config) {
            // Retry the original request with new token
            const token = authService.getAccessToken();
            if (token && error.config.headers) {
              error.config.headers.Authorization = `Bearer ${token}`;
            }
            return api.request(error.config);
          }
        } catch (refreshError) {
          console.error('Token refresh failed, redirecting to login:', refreshError);
          // Redirect to login
          keycloak.login();
        }
      }
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data);
    }

    // Transform error response
    const responseData = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    const apiError: ApiError = {
      message: responseData?.message || error.message || 'An unexpected error occurred',
      errors: responseData?.errors,
      statusCode: error.response?.status || 500,
    };

    return Promise.reject(apiError);
  }
);

export default api;

// Export convenience methods
export const apiClient = {
  get: api.get,
  post: api.post,
  put: api.put,
  patch: api.patch,
  delete: api.delete,
};
